import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router';
import { SVG, Icons } from '../../atoms/SVG';
import extendClassNameWithModifier from '../../../lib/bemUtils/bem';
import { useGlobalCollapsible } from '../../../_lib/components/CollapsibleExpander';
import { CollapsibleBlockModifier } from './CollapsibleBlock.modifier';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { Heading } from '../../../_lib/components/Heading/Heading';

type CollapsibleBlockProps = {
  title: string;
  closed: boolean;
  id: string;
  modifier?: Modifier<CollapsibleBlockModifier>;
  hierarchy?: number;
};

export function CollapsibleBlock({
  title,
  children,
  closed = true,
  id,
  modifier,
  hierarchy = 2,
}: PropsWithChildren<CollapsibleBlockProps>) {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const collapsibleBlockHeadingId = `collapsible-block-heading-${id}`;
  const collapsibleBlockId = `collapsible-block-${id}`;
  const { isOpen, toggle, open } = useGlobalCollapsible(collapsibleBlockId, !closed);

  const [containsWysiwygElement, setContainsWysiwygElement] = useState(false);
  const collapsibleBlockRef = useRef<HTMLDivElement>(null);

  // opens collapsible when hash changes and heading with matching id is found (needed for TableOfContents component)
  useEffect(() => {
    if (hash?.includes(id)) {
      open();
    }
  }, [hash, id, open]);

  /* Need to remove hash when corresponding collapsible is closed to allow reopening
    via table of contents (compare SIBZIEL-11198).
    Explicitly not having hash in dependency list as it will remove hash immediately when it is set.
   */
  useEffect(() => {
    if (!isOpen && hash?.includes(id)) {
      // there is no other way in react to remove the hash from the url than using navigate
      navigate(pathname);
    }
  }, [id, isOpen, navigate, pathname]);

  useEffect(() => {
    const wysiwygElement = collapsibleBlockRef.current?.querySelector('.wysiwyg');

    if (wysiwygElement) {
      setContainsWysiwygElement(true);
    }
  }, []);

  return (
    <>
      <Heading
        hierarchy={hierarchy}
        id={collapsibleBlockHeadingId}
        className={extendClassNameWithModifier('collapsible-block', modifier)}
      >
        <button
          className={extendClassNameWithModifier(
            'collapsible-block__button',
            containsWysiwygElement ? 'collapsible-block__button--wysiwyg' : '',
          )}
          type="button"
          aria-expanded={isOpen ? 'true' : 'false'}
          aria-controls={collapsibleBlockId}
          onClick={toggle}
        >
          <span className="collapsible-block__title">
            <span className="collapsible-block__text">{title}</span>
          </span>
          <span className="collapsible-block__icon">
            <SVG svgSpriteId={Icons.ARROW_DOWN} />
          </span>
        </button>
      </Heading>
      <div
        ref={collapsibleBlockRef}
        className="collapsible-block__panel"
        aria-hidden={!isOpen ? 'true' : 'false'}
        id={collapsibleBlockId}
      >
        {children}
      </div>
    </>
  );
}
